import { useCallback, useEffect, useState } from 'react';

import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';

import { usePathname } from 'src/routes/hooks';

import { navSectionClasses } from '../classes';
import { navSectionCssVars } from '../css-vars';
import { NavLi, NavUl, Subheader } from '../styles';
import { NavList, isNavItemActive } from './nav-list';

import { useTranslate } from 'react-admin';
import type { NavGroupProps, NavSectionProps } from '../types';

// ----------------------------------------------------------------------

function checkItemsActive(items: any[], pathname: string): boolean {
  return items?.some((item: any) => isNavItemActive(item, pathname));
}

// ----------------------------------------------------------------------

export function NavSectionVertical({
  sx,
  data,
  render,
  slotProps,
  enabledRootRedirect,
  cssVars: overridesVars,
}: NavSectionProps) {
  const theme = useTheme();
  const pathname = usePathname();

  const cssVars = {
    ...navSectionCssVars.vertical(theme),
    ...overridesVars,
  };

  // Accordion: only one section expanded at a time.
  // Recomputed every render so it re-syncs when `data` arrives (e.g. permissions
  // load async on reload) — not just when the pathname changes.
  const activeSectionIndex = data.findIndex((group) => checkItemsActive(group.items, pathname));
  const activeSection = activeSectionIndex === -1 ? null : activeSectionIndex;

  const [openSection, setOpenSection] = useState<number | null>(activeSection);

  useEffect(() => {
    if (activeSection !== null) {
      setOpenSection(activeSection);
    }
  }, [activeSection]);

  const handleToggleSection = useCallback((index: number) => {
    setOpenSection((prev) => (prev === index ? null : index));
  }, []);

  return (
    <Stack component="nav" className={navSectionClasses.vertical.root} sx={{ ...cssVars, ...sx }}>
      <NavUl sx={{ flex: '1 1 auto', gap: 'var(--nav-item-gap)' }}>
        {data.map((group, index) => (
          <Group
            key={group.subheader ?? group.items[0].title}
            subheader={group.subheader}
            items={group.items}
            render={render}
            slotProps={slotProps}
            enabledRootRedirect={enabledRootRedirect}
            open={openSection === index}
            onToggle={() => handleToggleSection(index)}
          />
        ))}
      </NavUl>
    </Stack>
  );
}

// ----------------------------------------------------------------------

function Group({ items, render, subheader, slotProps, enabledRootRedirect, open, onToggle }: NavGroupProps) {
  const pathname = usePathname();
  const t = useTranslate();

  // Accordion: only one direct child of this section expanded at a time.
  // Recomputed every render so it re-syncs when `items` arrive (e.g. permissions
  // load async on reload) — not just when the pathname changes.
  const activeItemIndex = items.findIndex((item) => isNavItemActive(item, pathname));
  const activeIndex = activeItemIndex === -1 ? null : activeItemIndex;

  const [openIndex, setOpenIndex] = useState<number | null>(activeIndex);

  useEffect(() => {
    if (activeIndex !== null) {
      setOpenIndex(activeIndex);
    }
  }, [activeIndex]);

  const handleToggleItem = useCallback((index: number) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  }, []);

  const renderContent = (
    <NavUl sx={{ gap: 'var(--nav-item-gap)' }}>
      {items.map((list, index) => (
        <NavList
          key={list.title}
          data={list}
          render={render}
          depth={1}
          slotProps={slotProps}
          enabledRootRedirect={enabledRootRedirect}
          open={openIndex === index}
          onToggle={() => handleToggleItem(index)}
        />
      ))}
    </NavUl>
  );

  return (
    <NavLi>
      {subheader ? (
        <>
          <Subheader
            data-title={t(subheader)}
            open={open}
            onClick={onToggle}
            sx={slotProps?.subheader}
          >
            {t(subheader)}
          </Subheader>

          <Collapse in={open}>{renderContent}</Collapse>
        </>
      ) : (
        renderContent
      )}
    </NavLi>
  );
}
